import { HttpErrorResponse } from '@angular/common/http';

import { Observable, of, throwError } from 'rxjs';
import { delay, filter, mergeMap, retryWhen, switchMapTo } from 'rxjs/operators';

import { IRequestsNestedState } from './interfaces/requests.interface';

export function updateEntities(payload, entityModel?, idFieldName = 'id') {
  if (!Array.isArray(payload)) {
    payload = [payload];
  }
  // spreading the accumulator per entity made this quadratic in the fleet size
  const entities: any = {};
  const ids = [];
  for (const entity of payload) {
    const id = entity[idFieldName];
    entities[id] = entityModel ? new entityModel(entity) : entity;
    ids.push(id);
  }
  return { ids, entities };
}

export function updateEntity(payload, entityModel?) {
  if (!Array.isArray(payload)) {
    payload = [payload];
  }

  const result = payload.reduce(
    (acc: any, entity: any) => {
      return { ...acc, entity: entityModel ? new entityModel(entity) : entity };
    },
    {},
  );
  return result ? result.entity : result;
}

// stream operator to wait till request finish
export function newRequestFinished(): (obs: Observable<IRequestsNestedState>) => Observable<IRequestsNestedState> {
  return (obs: Observable<IRequestsNestedState>) => {
    return obs.pipe(
      filter((requestState: IRequestsNestedState) => requestState.loading),
      switchMapTo(
        obs.pipe(
          filter((requestState: IRequestsNestedState) => requestState.loaded),
        ),
      ),
    );
  };
}



// stream operator to retry failed request with delay
export function delayedRetry(delayMs: number, maxRetry = 3): (src: Observable<any>) => Observable<any> {
  let retries = maxRetry;

  return (obs$: Observable<any>): Observable<any> => {
    return obs$.pipe(
      retryWhen((errors) => {
        return errors.pipe(
          delay(delayMs),
          mergeMap(err => {
            return retries-- ? of(err) : throwError(new HttpErrorResponse({
              error: { error: `Request failed after ${maxRetry} retries` },
            }));
          }),
        );
      }),
    );
  };
}




// generate random color
export function generateRandomColor(): string {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    // avoid light colors
    const multiplier = i === 0 ? 15 : 16;
    color += letters[Math.floor(Math.random() * multiplier)];
  }
  return color;
}


// Returns null rather than throwing on a token that is absent or not a JWT.
// Callers guard on the parsed result, and those guards never got the chance to
// run while a missing token threw out of token.split() first.
export function parseJwt(token: string, asString = true) {
  if (!token) {
    return null;
  }

  try {
    const base64Url: string = token.split('.')[1];
    if (!base64Url) {
      return null;
    }

    const base64: string = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload: string = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return asString ? jsonPayload : JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}
